import { get } from 'lodash-es';

import { getFoundation } from '../foundation';

import type { CSSObject } from '@emotion/react';
import type { ThemeOptions } from '@mui/material';
import type {
  TypographyVariant as MuiTypographyVariant,
  TypographyVariantsOptions,
} from '@mui/material/styles/createTypography';
import type { Foundation } from '@kep/kcds-foundation';
import type { ValueOf } from '@kc-console/core';
import type { NestedKeyOf, ReplaceWord } from '../../types/utils';
import type { ThemeMode } from '../foundation';

// NOTE (@walter-24) foundation의 typography 관련 key, 실제 foundation 객체에서 typography만 추출하기 위한 key 배열
const typographies = ['heading', 'label', 'body', 'code'] as const;

// NOTE (@walter-24) foundation의 typography 관련 key 조합들
type TypographyKey = NestedKeyOf<Pick<Foundation, ValueOf<typeof typographies>>>;

// NOTE (@walter-24) typography 관련 key는 3depth로 타겟팅함
type Filter3DepthKey<K extends string> = K extends `${string}.${string}.${string}`
  ? K extends `${string}.${string}.${string}.${string}`
    ? never
    : K
  : never;

// NOTE (@walter-24) foundation typography variant 선언 (`heading.bold.xl` -> `heading/bold/xl`로 변환)
export type TypographyVariant = 'inherit' | ReplaceWord<Filter3DepthKey<TypographyKey>, '.', '/'>;

// NOTE MUI Typography를 덮어쓸 Theme 객체 타입, foundation variant는 적용
type CustomTypographyVariants = Record<TypographyVariant, CSSObject>;

declare module '@mui/material/styles/createTypography' {
  interface TypographyVariants extends CustomTypographyVariants {}
  interface TypographyVariantsOptions extends CustomTypographyVariants {}
}

export const createTypography = (themeMode: ThemeMode): ThemeOptions['typography'] => {
  const foundation = getFoundation(themeMode);

  // NOTE (@walter-24) foundation에서 typography 관련 key만 추출
  const allTypographyPaths = typographies.reduce((acc, key) => {
    acc.push(...getNestedKeyOf(foundation[key], key));
    return acc;
  }, [] as string[]);

  // NOTE (@walter-24) foundation에서 typography 관련 key 중 3depth인 것만 추출
  const typographyVariants = allTypographyPaths.filter(key => key.split('.').length === 3);

  const staticTypographyOptions = {
    inherit: {
      fontSize: 'inherit',
      fontWeight: 'inherit',
      fontFamily: 'inherit',
      lineHeight: 'inherit',
      letterSpacing: 'inherit',
      textDecoration: 'inherit',
    },
  } as TypographyVariantsOptions;

  // NOTE (@walter-24) { "heading/bold/xl": { ... } } 형태로 객체 생성
  const activeTypographyOptions = typographyVariants.reduce((acc, key) => {
    // NOTE (@walter-24) `heading.bold.xl` -> `heading/bold/xl`로 변환
    const transformedKey = key.replace(/\./g, '/') as TypographyVariant;
    acc[transformedKey] = get(foundation, key);
    return acc;
  }, {} as TypographyVariantsOptions);

  const disabledTypographyOptions = [
    'h1',
    'h2',
    'h3',
    'h4',
    'h5',
    'h6',
    'subtitle1',
    'subtitle2',
    'body1',
    'body2',
    'caption',
    'button',
    'overline',
  ].reduce((acc, key) => {
    acc[key as MuiTypographyVariant] = {};
    return acc;
  }, {} as TypographyVariantsOptions);

  return {
    fontFamily: foundation.fontFamilies.base,
    fontSize: foundation.body.normal.md.fontSize,
    htmlFontSize: foundation.body.normal.md.fontSize,
    ...staticTypographyOptions,
    ...activeTypographyOptions,
    ...disabledTypographyOptions,
  };
};

const getNestedKeyOf = (object: Record<string, any>, parent = ''): string[] => {
  const result: string[] = [];
  for (const key in object) {
    const path = [...parent.split('.'), key].filter(Boolean).join('.');
    result.push(path);
    if (typeof object[key] === 'object') {
      result.push(...getNestedKeyOf(object[key], path));
    }
  }
  return result;
};
